import { Component, EventEmitter, Input, OnInit, inject } from '@angular/core';
import { ModalDialogModule } from '@vg-constellation/angular-20/modal-dialog';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, Subscription, take } from 'rxjs';

import { BulkRemovePermissionRequestPayload, bulkRemovePermission } from '../../../../store/actions/access-permissions.actions';
import { selectUserInfo } from '../../../../store/selectors/account-permissions.selector';
import { selectRemoveUserStepperData } from '../../../../store/selectors/grant-revoke-permissions.selector';
import accountPermissionUtils from '../../../../utils/account-permission/account-permission.util';
import utils from '../../../../utils/account-permission/account-permission.util';
import { Unsubscribe } from '../../../../utils/decorators/dev';
import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { RemoveUserStepBaseComponent } from '../remove-user-step-base.component';
import { adobeConfigData } from './raf-review-step-adobe';
import { AdobeAnalyticsProcessStep } from '../../../../services/adobe-launch/adobe-launch.process';
import { LoggerService } from '../../../../services/logger/logger.service';
import { ContentUtil } from '../../../../utils/content/content.util';
import { UserDetailsDto } from 'src/common/dtos/user-details.dto';
import { AsyncPipe, NgFor } from '@angular/common';

@AdobeTagging(adobeConfigData)
@Unsubscribe
@Component({
    selector: 'zci-raf-review-step',
    templateUrl: './raf-review-step.component.html',
    styleUrls: ['./raf-review-step.component.scss'],
    imports: [
        AsyncPipe,
        NgFor,
        ModalDialogModule,
    ],

    
})
export class RafReviewStepComponent extends RemoveUserStepBaseComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly logger = inject(LoggerService);

    @Input() nextStepClicked: EventEmitter<any> = new EventEmitter<any>();
    @Input() nextStepReady: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() locationChanged: EventEmitter<any>;

    description = this.store.select(selectRemoveUserStepperData).pipe(
        filter(data => !!data.selectedUser),
        map(({ selectedUser }) => ContentUtil.grantorFragmentContent.remove_user.frmcRemoveUserReviewSubmit.body.replace(
            '${granteeName}', this.constructUserName(selectedUser)))
    );

    selectedAccounts = this.store.select(selectRemoveUserStepperData).pipe(
        filter(data => Array.isArray(data.selectedAccounts)),
        map(({ selectedAccounts }) => selectedAccounts)
    );

    subs = new Subscription();

    constructUserName: (user: UserDetailsDto) => string = accountPermissionUtils.constructUserName;

    ngOnInit(): void {
        this.nextStepClicked.subscribe(this.onNextStepLoad.bind(this));
    }

    onNextStepLoad() {
        this.subs.add(
            combineLatest([
                this.store.select(selectRemoveUserStepperData),
                this.store.select(selectUserInfo),
            ])
                .pipe(take(1))
                .subscribe(([removeUserStepperData, grantorInfo]) => {
                    const { selectedUser, selectedAccounts } = removeUserStepperData;
                    const granteeName = selectedUser.firstName + ' ' + selectedUser.lastName;
                    const grantorName = grantorInfo.firstName + ' ' + grantorInfo.lastName;

                    this.logger.info(
                        `[RafReviewStep] Submitting remove access request. ` +
                        `Grantor POID: ${grantorInfo?.clientPoid}, ` +
                        `Grantee POID: ${selectedUser?.clientPoid}, ` +
                        `Number of accounts: ${selectedAccounts?.length ?? 0}`,
                    );
                    const accountsByAccessLevel = selectedAccounts.reduce(
                        (map, account) => {
                            const accessLevel = utils.getAccessLevel(
                                account.grantees,
                                selectedUser.clientPoid,
                                this.logger.warn,
                            );
                            if (!map[accessLevel]) {
                                map[accessLevel] = [];
                            }
                            map[accessLevel].push(account);
                            return map;
                        },
                        {} as Record<string, typeof selectedAccounts>,
                    );

                    // One request payload per distinct access level
                    const requests: BulkRemovePermissionRequestPayload[] = Object.entries(
                        accountsByAccessLevel,
                    ).map(([accessLevel, accounts]) => ({
                        granteeDetails: {
                            granteeId: selectedUser.clientPoid,
                            granteeName,
                            accessLevel: accessLevel as any,
                            newGrantee: false,
                        },
                        grantorDetails: {
                            ownerId: grantorInfo.clientPoid,
                            grantorName,
                        },
                        accounts: accounts.map((account) => ({
                            accountId: account.accountId,
                            accountName: account.accountName,
                            serviceAgreementId: account.serviceAgreementId,
                            isVBA: account.isVBA,
                        })),
                    }));

                    this.store.dispatch(bulkRemovePermission({ requests }));
                }),
        );
    }

    protected readonly AdobeAnalyticsProcessStep = AdobeAnalyticsProcessStep;
}